// billingReturn.ts — what Dashboard does when Stripe sends the user back.
//
// Two return paths:
//   ?checkout=success — Stripe Checkout finished. The plan change arrives by
//                       webhook, which can land after the redirect (#89).
//   ?billing=updated  — the Customer Portal's return URL. The user may have
//                       switched plans, cancelled, or only updated a card.
//
// Either way the plan the app holds, AND the property rows it already
// fetched (the server decides which rows come back locked from the plan), can
// be stale. pollPlan is the injectable polling loop; Dashboard supplies the
// fetch and what "settled" means for each path.

import type { UserPlan } from "./planCapabilities";
import type { BillingInterval, PaidPlanIntent } from "./pendingIntent";

//   ?checkout=cancel  — the user left Stripe Checkout without paying. Until
//                       2026-10-19 nothing handled it: the buyer landed on
//                       the map with no message (one user started checkout
//                       three times and never paid).
export type BillingReturnKind = "checkout" | "portal" | "cancel";

export function billingReturnKind(search: string): BillingReturnKind | null {
  const params = new URLSearchParams(search);
  if (params.get("checkout") === "success") return "checkout";
  if (params.get("checkout") === "cancel") return "cancel";
  if (params.get("billing") === "updated") return "portal";
  return null;
}

export interface CheckoutReturn {
  plan: PaidPlanIntent | null;
  interval: BillingInterval | null;
  /** True when the session was a free trial (nothing charged yet). */
  trial: boolean;
}

/** What the API put on the Stripe success/cancel URL (plan, interval,
 *  trial=0|1). Tolerates their absence: older checkout sessions, or a URL
 *  someone typed. */
export function parseCheckoutReturn(search: string): CheckoutReturn {
  const params = new URLSearchParams(search);
  const plan = params.get("plan");
  const interval = params.get("interval");
  return {
    plan: plan === "scout" || plan === "pro" ? plan : null,
    interval: interval === "monthly" || interval === "annual" ? interval : null,
    trial: params.get("trial") === "1",
  };
}

/** pathname + search with the billing-return params removed, preserving
 *  everything else (e.g. ?property=<id>). */
export function stripBillingParams(pathname: string, search: string): string {
  const next = new URLSearchParams(search);
  for (const k of ["checkout", "billing", "plan", "interval", "trial"]) next.delete(k);
  const qs = next.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

export const BILLING_POLL_MAX_ATTEMPTS = 15; // 15 × 2s = 30s ceiling
export const BILLING_POLL_INTERVAL_MS = 2000;

/** Kept for callers that still import it; prefer checkoutSettledMessage. */
export const CHECKOUT_ACTIVATING_MESSAGE =
  "Checkout finished — your access is still activating. Refresh the page in a minute to see everything unlocked.";

const PLAN_LABEL: Record<PaidPlanIntent, string> = { scout: "Scout", pro: "Pro" };

/** What the Dashboard says when the user lands back from Stripe Checkout.
 *  "Payment received" was shown for $0 trial starts until 2026-10-19; a
 *  trialist's first question is "was I just charged?", so say so. */
export function checkoutSettledMessage(r: { settled: boolean; trial: boolean; plan: PaidPlanIntent | null }): string {
  const label = r.plan ? PLAN_LABEL[r.plan] : "paid";
  if (!r.settled) return CHECKOUT_ACTIVATING_MESSAGE;
  if (r.trial) {
    return `Your 14-day ${label} trial is on. Nothing is charged until it ends — cancel any time from Settings → Manage subscription.`;
  }
  return `You're on ${label}. Everything is unlocked — thanks for subscribing.`;
}

export interface PollPlanOptions {
  fetchPlan: () => Promise<UserPlan | undefined>;
  /** True when this response is the change we are waiting for. */
  isSettled: (plan: UserPlan) => boolean;
  isCancelled: () => boolean;
  maxAttempts?: number;
  intervalMs?: number;
  sleep?: (ms: number) => Promise<void>;
}

export interface PollPlanResult {
  settled: boolean;
  plan?: UserPlan;
  attempts: number;
}

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** Poll until isSettled, the attempt cap, or cancellation. Fetch errors are
 *  swallowed and retried: network blips are common right after a redirect. */
export async function pollPlan(opts: PollPlanOptions): Promise<PollPlanResult> {
  const max = opts.maxAttempts ?? BILLING_POLL_MAX_ATTEMPTS;
  const interval = opts.intervalMs ?? BILLING_POLL_INTERVAL_MS;
  const sleep = opts.sleep ?? defaultSleep;
  let attempts = 0;
  while (!opts.isCancelled() && attempts < max) {
    attempts++;
    try {
      const plan = await opts.fetchPlan();
      if (opts.isCancelled()) break;
      if (plan && opts.isSettled(plan)) return { settled: true, plan, attempts };
    } catch {
      /* keep polling */
    }
    if (attempts < max) await sleep(interval);
  }
  return { settled: false, attempts };
}
